import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Toast, Modal, StatCard } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      return await res.json();
    } catch (err) { return {}; }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  },
  uploadFile: async (endpoint, formData, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  }
};

function BulkImportPage({ token }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldDescriptions, setFieldDescriptions] = useState(null);
  const [showFields, setShowFields] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [sample, setSample] = useState(null);
  const [previewCols, setPreviewCols] = useState(null); // null = use defaults
  const [showPreviewCols, setShowPreviewCols] = useState(false);
  
  const DEFAULT_PREVIEW_COLS = ['admission_number', 'name', 'class_name', 'section_name', 'father_name', 'samagra_id', 'caste_category'];
  const colLabel = (c) => ({
    admission_number: 'प्रवेश क्र.', name: 'नाम', name_hindi: 'नाम (हिंदी)', gender: 'लिंग', dob: 'जन्म तिथि',
    class_name: 'कक्षा', section_name: 'सेक्शन', primary_phone: 'फोन', father_name: 'पिता', mother_name: 'माता',
    samagra_id: 'समग्र ID', aadhaar_number: 'आधार', caste_category: 'श्रेणी', religion: 'धर्म',
  }[c] || c);
  
  useEffect(() => { loadFieldDescriptions(); }, []);

  const parseCsv = (text) => {
    const clean = text.replace(/^\uFEFF/, '').trim();
    const lines = clean.split(/\r?\n/).filter(Boolean);
    return lines.map((line) => {
      const cells = [];
      let cur = '', inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQ = !inQ; }
        else if (ch === ',' && !inQ) { cells.push(cur); cur = ''; }
        else { cur += ch; }
      }
      cells.push(cur);
      return cells;
    });
  };

  const toggleSample = async () => {
    if (showSample) { setShowSample(false); return; }
    setShowSample(true);
    if (sample) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk/template/basic/download`);
      const text = await res.text();
      const rows = parseCsv(text);
      setSample({ headers: rows[0] || [], rows: rows.slice(1, 6) });
    } catch (err) {
      setToast({ message: 'Sample data लोड करने में त्रुटि', type: 'error' });
      setShowSample(false);
    }
  };
  
  const loadFieldDescriptions = async () => {
    try {
      const res = await api.get('/api/bulk/template/fields', token);
      setFieldDescriptions(res);
    } catch (err) {
      console.error('Load fields failed:', err);
    }
  };
  
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setResult(null);
    
    if (selectedFile) {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk/students/preview`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        setPreview(data);
      } catch (err) {
        setToast({ message: 'Preview में त्रुटि', type: 'error' });
      }
      setLoading(false);
    }
  };
  
  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk/students/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      setResult(data);
      setToast({ message: `${data.imported || 0} विद्यार्थी import हुए!`, type: data.imported > 0 ? 'success' : 'error' });
    } catch (err) {
      setToast({ message: 'Upload में त्रुटि', type: 'error' });
    }
    setLoading(false);
  };
  
  // Synchronous anchor click within the user-gesture so Chrome never blocks the download.
  // Uses the public (no-auth) endpoints that serve static sample templates.
  const triggerDownload = (path, filename) => {
    const a = document.createElement('a');
    a.href = `${process.env.REACT_APP_BACKEND_URL}${path}`;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); }, 100);
  };

  const downloadBasicTemplate = () => {
    triggerDownload('/api/bulk/template/basic/download', 'student_import_basic_template.csv');
    setToast({ message: 'Basic template download हो रहा है! ✅', type: 'success' });
  };

  const downloadFullTemplate = () => {
    triggerDownload('/api/bulk/template/full/download', 'student_import_full_template.csv');
    setToast({ message: 'Full template download हो रहा है! ✅', type: 'success' });
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Bulk Import / बल्क आयात</h2>
      </div>
      
      {/* Step 1: Download Template */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold mb-4 text-lg">Step 1: Sample Template Download करें</h3>
        <p className="text-gray-600 mb-4">
          CSV template download करें, विद्यार्थियों का डेटा भरें, फिर upload करें।
        </p>
        
        <div className="flex flex-wrap gap-3">
          <button onClick={downloadBasicTemplate} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2" data-testid="download-basic-template">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Basic Template (17 fields)
          </button>
          <button onClick={downloadFullTemplate} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2" data-testid="download-full-template">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Full Template (55 सरकारी fields)
          </button>
          <button onClick={() => setShowFields(!showFields)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            {showFields ? 'Fields छुपाएं' : 'सभी Fields देखें'}
          </button>
          <button onClick={toggleSample} className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 flex items-center gap-2" data-testid="toggle-sample-data">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            {showSample ? 'Sample छुपाएं' : 'Sample भरी हुई CSV देखें'}
          </button>
        </div>

        {/* Sample Filled CSV Preview */}
        {showSample && (
          <div className="mt-4 border border-amber-200 rounded-lg overflow-hidden" data-testid="sample-data-table">
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm font-medium text-amber-800">
              यह नमूना डेटा है — इसी तरह अपने विद्यार्थियों की जानकारी भरें (Basic Template)
            </div>
            {!sample ? (
              <div className="p-4 text-sm text-gray-500">Sample लोड हो रहा है...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {sample.headers.map((h, i) => (
                        <th key={i} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sample.rows.map((row, ri) => (
                      <tr key={ri} className={ri % 2 ? 'bg-gray-50' : 'bg-white'}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-2 text-gray-700 whitespace-nowrap border-b border-gray-100">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* Direct Download Links (Backup) */}
        <div className="mt-2 text-xs text-gray-500">
          <span>अगर बटन काम न करे तो यहाँ से download करें: </span>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/bulk/template/basic/download`} 
             download="student_import_basic_template.csv"
             className="text-blue-600 hover:underline mx-1">Basic Template</a>
          <span>|</span>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/bulk/template/full/download`} 
             download="student_import_full_template.csv"
             className="text-green-600 hover:underline mx-1">Full Template</a>
        </div>
        
        {/* Required Fields */}
        <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-sm font-medium text-orange-800 mb-2">आवश्यक Fields (Required):</p>
          <div className="flex flex-wrap gap-2">
            {fieldDescriptions?.required_fields?.map(f => (
              <span key={f} className="px-2 py-1 bg-orange-100 border border-orange-300 rounded text-xs font-medium text-orange-800">{f}</span>
            ))}
          </div>
        </div>
        
        {/* All Fields */}
        {showFields && fieldDescriptions?.fields && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg max-h-80 overflow-y-auto">
            <p className="text-sm font-medium mb-3">सभी उपलब्ध Fields:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(fieldDescriptions.fields).map(([field, desc]) => (
                <div key={field} className="text-xs p-2 bg-white rounded border">
                  <span className="font-mono font-medium text-blue-600">{field}</span>
                  <span className="text-gray-500 ml-2">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Instructions */}
        {fieldDescriptions?.instructions && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">Format Guide:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {Object.entries(fieldDescriptions.instructions).map(([field, inst]) => (
                <div key={field} className="flex gap-2">
                  <span className="font-medium text-blue-700">{field}:</span>
                  <span className="text-gray-600">{inst}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Step 2: Upload CSV */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold mb-4 text-lg">Step 2: CSV File Upload करें</h3>
        <input 
          type="file" 
          accept=".csv,.xlsx,.xls" 
          onChange={handleFileChange} 
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
          data-testid="file-upload-input"
        />
        
        {loading && <div className="mt-4 text-center text-gray-500">Processing...</div>}
        
        {preview && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">Preview (पहले 10 rows):</h4>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowPreviewCols(true)}
                  className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm flex items-center gap-1"
                  data-testid="preview-column-filter-btn"
                >
                  <i className="fa-solid fa-table-columns"></i> Columns ({(previewCols || DEFAULT_PREVIEW_COLS).length})
                </button>
                <span className="text-sm text-gray-500">कुल: {preview.total_records} records</span>
              </div>
            </div>
            {(() => {
              const avail = preview.available_columns || [];
              const cols = (previewCols || DEFAULT_PREVIEW_COLS).filter((c) => avail.length === 0 || avail.includes(c));
              const shown = cols.length ? cols : DEFAULT_PREVIEW_COLS;
              return (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left border">क्र.</th>
                    {shown.map((c) => <th key={c} className="px-3 py-2 text-left border whitespace-nowrap">{colLabel(c)}</th>)}
                    <th className="px-3 py-2 text-left border">स्थिति</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview?.map((row, idx) => (
                    <tr key={idx} className={row.is_duplicate ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-2 border">{row.row}</td>
                      {shown.map((c) => (
                        <td key={c} className="px-3 py-2 border text-gray-600 whitespace-nowrap">
                          {c === 'caste_category' && row[c]
                            ? <span className={`px-2 py-0.5 rounded text-xs ${row[c] === 'sc' ? 'bg-blue-100 text-blue-700' : row[c] === 'st' ? 'bg-green-100 text-green-700' : row[c] === 'obc' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>{String(row[c]).toUpperCase()}</span>
                            : (row[c] !== undefined && row[c] !== '' ? String(row[c]) : '-')}
                        </td>
                      ))}
                      <td className="px-3 py-2 border">
                        {row.is_duplicate ? (
                          <span className="text-red-600 font-medium">डुप्लिकेट</span>
                        ) : (
                          <span className="text-green-600">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              );
            })()}
            
            {preview.parse_errors?.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm font-medium text-yellow-800">Parsing Warnings:</p>
                <ul className="text-xs text-yellow-700 mt-1">
                  {preview.parse_errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {showPreviewCols && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPreviewCols(false)}>
                <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="preview-column-modal">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-gray-800">📊 Preview Columns चुनें</h3>
                    <button onClick={() => setShowPreviewCols(false)} className="text-gray-500 text-2xl">&times;</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto">
                    {(preview.available_columns || DEFAULT_PREVIEW_COLS).map((c) => {
                      const cur = previewCols || DEFAULT_PREVIEW_COLS;
                      const checked = cur.includes(c);
                      return (
                        <label key={c} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const base = previewCols || DEFAULT_PREVIEW_COLS;
                              setPreviewCols(e.target.checked ? [...base, c] : base.filter((k) => k !== c));
                            }}
                            className="w-4 h-4 rounded text-orange-500"
                            data-testid={`preview-col-${c}`}
                          />
                          <span className="text-sm text-gray-700">{colLabel(c)}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-3 border-t flex gap-3">
                    <button onClick={() => setPreviewCols(DEFAULT_PREVIEW_COLS)} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Default</button>
                    <button onClick={() => setPreviewCols(preview.available_columns || DEFAULT_PREVIEW_COLS)} className="flex-1 py-2 border border-purple-300 bg-purple-50 rounded-lg text-purple-700 text-sm">सभी</button>
                    <button onClick={() => setShowPreviewCols(false)} className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm">Done</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Step 3: Import */}
      {preview && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold mb-4 text-lg">Step 3: विद्यार्थी Import करें</h3>
          <button 
            onClick={handleUpload} 
            disabled={loading}
            className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-medium text-lg flex items-center justify-center gap-2"
            data-testid="import-students-btn"
          >
            {loading ? (
              <>Processing...</>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                सभी विद्यार्थी Import करें
              </>
            )}
          </button>
          
          {result && (
            <div className={`mt-4 p-4 rounded-lg ${result.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className="font-semibold text-lg mb-2">Import Result:</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{result.imported}</p>
                  <p className="text-sm text-gray-500">सफल</p>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{result.failed}</p>
                  <p className="text-sm text-gray-500">विफल</p>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-gray-600">{result.total}</p>
                  <p className="text-sm text-gray-500">कुल</p>
                </div>
              </div>
              
              {result.details?.errors?.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">त्रुटियां:</p>
                  <ul className="text-sm text-red-600 space-y-1 max-h-32 overflow-y-auto">
                    {result.details.errors.map((e, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-red-400">•</span>
                        {typeof e === 'string' ? e : `Row ${e.row}: ${e.error}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {result.details?.imported_students?.length > 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-2">सफलतापूर्वक जोड़े गए:</p>
                  <div className="flex flex-wrap gap-2">
                    {result.details.imported_students.map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-white border border-green-200 rounded text-xs">
                        {s.admission_number} - {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Parent Portal Dashboard
function ParentPortalDashboard({ token, user }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  
  useEffect(() => { loadDashboard(); }, [selectedChild]);
  
  const loadDashboard = async () => {
    let url = '/api/portal/dashboard';
    if (selectedChild) url += `?student_id=${selectedChild}`;
    const res = await api.get(url, token);
    setDashboard(res);
    setLoading(false);
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  if (dashboard?.children && !selectedChild) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">Welcome, {user.name}!</h2>
        <p className="text-gray-600">Select a child to view details:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dashboard.children.map(child => (
            <button key={child.id} onClick={() => setSelectedChild(child.id)}
              className="bg-white rounded-xl shadow p-6 text-left hover:shadow-lg transition">
              <h3 className="text-lg font-semibold">{child.name}</h3>
              <p className="text-gray-500">Click to view details</p>
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{dashboard?.student?.name}</h2>
          <p className="text-gray-500">{dashboard?.student?.class_name} • {dashboard?.student?.school_name}</p>
        </div>
        {user.role === 'parent' && dashboard?.children_count > 1 && (
          <button onClick={() => setSelectedChild(null)} className="text-orange-600 hover:text-orange-700">
            ← Change Child
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-6 text-white">
          <h3 className="text-lg opacity-80">Attendance</h3>
          <p className="text-3xl font-bold mt-2">{dashboard?.attendance?.percentage || 0}%</p>
          <p className="text-sm opacity-80 mt-1">{dashboard?.attendance?.present_days}/{dashboard?.attendance?.total_days} days</p>
        </div>
        
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-6 text-white">
          <h3 className="text-lg opacity-80">Fees Paid</h3>
          <p className="text-3xl font-bold mt-2">₹{dashboard?.fees?.paid || 0}</p>
          <p className="text-sm opacity-80 mt-1">of ₹{dashboard?.fees?.total_fee || 0}</p>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
          <h3 className="text-lg opacity-80">Pending Fees</h3>
          <p className="text-3xl font-bold mt-2">₹{dashboard?.fees?.pending || 0}</p>
          <p className="text-sm opacity-80 mt-1">{dashboard?.fees?.pending > 0 ? 'Due' : 'All Paid!'}</p>
        </div>
      </div>
      
      {dashboard?.recent_announcements?.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold mb-4">Recent Announcements</h3>
          <div className="space-y-3">
            {dashboard.recent_announcements.map(ann => (
              <div key={ann.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium">{ann.title}</h4>
                  <span className="text-xs text-gray-500">{new Date(ann.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{ann.content?.substring(0, 100)}...</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Portal Attendance Page
function PortalAttendancePage({ token, user }) {
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  
  useEffect(() => { loadAttendance(); }, [month]);
  
  const loadAttendance = async () => {
    const studentId = user.student_id || user.children?.[0]?.id;
    if (!studentId) {
      setLoading(false);
      return;
    }
    const res = await api.get(`/api/portal/attendance/${studentId}?month=${month}`, token);
    setAttendance(res);
    setLoading(false);
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Attendance History</h2>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-4 py-2 border rounded-lg" />
      </div>
      
      {attendance?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total Days" value={attendance.summary.total_days} icon="📅" color="blue" />
          <StatCard title="Present" value={attendance.summary.present} icon="✅" color="green" />
          <StatCard title="Absent" value={attendance.summary.absent} icon="❌" color="red" />
          <StatCard title="Percentage" value={`${attendance.summary.percentage}%`} icon="📊" color="orange" />
        </div>
      )}
      
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold mb-4">Daily Records</h3>
        <div className="grid grid-cols-7 gap-2">
          {attendance?.attendance?.map((record, idx) => (
            <div key={idx} className={`p-2 rounded text-center text-sm ${record.status === 'present' ? 'bg-green-100' : record.status === 'absent' ? 'bg-red-100' : 'bg-yellow-100'}`}>
              <div className="text-xs text-gray-500">{new Date(record.date).getDate()}</div>
              <div className="text-xs capitalize">{record.status}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Portal Fees Page
function PortalFeesPage({ token, user }) {
  const [fees, setFees] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { loadFees(); }, []);
  
  const loadFees = async () => {
    const studentId = user.student_id || user.children?.[0]?.id;
    if (!studentId) {
      setLoading(false);
      return;
    }
    const res = await api.get(`/api/portal/fees/${studentId}`, token);
    setFees(res);
    setLoading(false);
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Fee Details</h2>
      
      {fees?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard title="Total Fee" value={`₹${fees.summary.total_fee}`} icon="💰" color="blue" />
          <StatCard title="Paid" value={`₹${fees.summary.total_paid}`} icon="✅" color="green" />
          <StatCard title="Pending" value={`₹${fees.summary.pending}`} icon="⏳" color={fees.summary.pending > 0 ? 'red' : 'green'} />
        </div>
      )}
      
      {fees?.fee_plan && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold mb-4">Fee Structure</h3>
          <div className="space-y-2">
            {fees.fee_plan.fee_heads?.map((head, idx) => (
              <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded">
                <span>{head.name}</span>
                <span>₹{head.amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {fees?.payments?.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold mb-4">Payment History</h3>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Receipt</th>
                <th className="px-4 py-2 text-left">Amount</th>
                <th className="px-4 py-2 text-left">Method</th>
                <th className="px-4 py-2 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {fees.payments.map((p, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-2">{p.receipt_number || '-'}</td>
                  <td className="px-4 py-2">₹{p.amount_paid}</td>
                  <td className="px-4 py-2 capitalize">{p.payment_method}</td>
                  <td className="px-4 py-2">{new Date(p.payment_date).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Portal Announcements Page
function PortalAnnouncementsPage({ token, user }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { loadAnnouncements(); }, []);
  
  const loadAnnouncements = async () => {
    const studentId = user.student_id || user.children?.[0]?.id;
    const res = await api.get(`/api/portal/announcements?student_id=${studentId}`, token);
    setAnnouncements(res.announcements || []);
    setLoading(false);
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Announcements</h2>
      
      <div className="space-y-4">
        {announcements.map(ann => (
          <div key={ann.id} className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start mb-2">
              <span className={`px-2 py-1 rounded text-xs capitalize ${ann.type === 'notice' ? 'bg-blue-100 text-blue-700' : ann.type === 'homework' ? 'bg-purple-100 text-purple-700' : ann.type === 'event' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {ann.type}
              </span>
              <span className="text-sm text-gray-500">{new Date(ann.created_at).toLocaleDateString()}</span>
            </div>
            <h3 className="font-semibold text-lg">{ann.title}</h3>
            <p className="text-gray-600 mt-2">{ann.content}</p>
            {ann.content_hindi && <p className="text-gray-500 mt-2">{ann.content_hindi}</p>}
          </div>
        ))}
        
        {announcements.length === 0 && (
          <div className="text-center py-12 text-gray-500">No announcements</div>
        )}
      </div>
    </div>
  );
}

// Portal Complaints Page
function PortalComplaintsPage({ token, user }) {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({ category: 'academic', subject: '', description: '' });
  
  useEffect(() => { loadComplaints(); }, []);
  
  const loadComplaints = async () => {
    const res = await api.get('/api/portal/complaints', token);
    setComplaints(res.complaints || []);
    setLoading(false);
  };
  
  const handleSubmit = async () => {
    const studentId = user.student_id || user.children?.[0]?.id;
    await api.post(`/api/portal/complaints?student_id=${studentId}&category=${formData.category}&subject=${encodeURIComponent(formData.subject)}&description=${encodeURIComponent(formData.description)}`, {}, token);
    setShowAdd(false);
    setFormData({ category: 'academic', subject: '', description: '' });
    loadComplaints();
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  const statusColors = { open: 'bg-red-100 text-red-700', in_progress: 'bg-yellow-100 text-yellow-700', resolved: 'bg-green-100 text-green-700', closed: 'bg-gray-100 text-gray-700' };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">My Complaints</h2>
        {user.role === 'parent' && (
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
            + File Complaint
          </button>
        )}
      </div>
      
      <div className="space-y-4">
        {complaints.map(c => (
          <div key={c.id} className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start mb-2">
              <div className="flex gap-2">
                <span className={`px-2 py-1 rounded text-xs ${statusColors[c.status]}`}>{c.status.replace('_', ' ')}</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-xs capitalize">{c.category}</span>
              </div>
              <span className="text-sm text-gray-500">{new Date(c.created_at).toLocaleDateString()}</span>
            </div>
            <h3 className="font-semibold">{c.subject}</h3>
            <p className="text-gray-600 mt-2">{c.description}</p>
          </div>
        ))}
        
        {complaints.length === 0 && <div className="text-center py-12 text-gray-500">No complaints filed</div>}
      </div>
      
      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="File New Complaint">
        <div className="space-y-4">
          <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
            <option value="academic">Academic</option>
            <option value="fee">Fee</option>
            <option value="behaviour">Behaviour</option>
            <option value="safety">Safety</option>
            <option value="transport">Transport</option>
            <option value="other">Other</option>
          </select>
          <input placeholder="Subject" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-32" />
          <button onClick={handleSubmit} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Submit Complaint</button>
        </div>
      </Modal>
    </div>
  );
}

// Exams Page
function ExamsPage({ token }) {
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showMarks, setShowMarks] = useState(null);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    name: '', exam_type: 'unit_test', class_id: '', subject_ids: [],
    start_date: '', end_date: '', max_marks: 100, passing_marks: 33
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [examsRes, subjectsRes, classesRes] = await Promise.all([
        api.get('/api/exams/', token),
        api.get('/api/exams/subjects', token),
        api.get('/api/classes/', token)
      ]);
      setExams(examsRes.exams || []);
      setSubjects(subjectsRes.subjects || []);
      setClasses(classesRes.classes || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.class_id) {
      setToast({ message: 'Name and Class required', type: 'error' });
      return;
    }
    const res = await api.post('/api/exams/', formData, token);
    if (res.success) {
      setToast({ message: 'Exam created!', type: 'success' });
      setShowAdd(false);
      setFormData({ name: '', exam_type: 'unit_test', class_id: '', subject_ids: [], start_date: '', end_date: '', max_marks: 100, passing_marks: 33 });
      loadData();
    }
  };

  const addSubject = async () => {
    const name = prompt('Enter subject name:');
    if (name) {
      await api.post(`/api/exams/subjects?name=${encodeURIComponent(name)}`, {}, token);
      loadData();
    }
  };

  const examTypes = { unit_test: 'Unit Test', monthly_test: 'Monthly Test', half_yearly: 'Half Yearly', annual: 'Annual' };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Exams & Results</h2>
        <div className="flex gap-2">
          <button onClick={addSubject} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">+ Subject</button>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">+ Create Exam</button>
        </div>
      </div>

      {subjects.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4">
          <h3 className="font-medium mb-2">Subjects</h3>
          <div className="flex flex-wrap gap-2">
            {subjects.map(s => <span key={s.id} className="px-3 py-1 bg-gray-100 rounded-full text-sm">{s.name}</span>)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exams.map(exam => (
          <div key={exam.id} className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start mb-2">
              <span className={`px-2 py-1 rounded text-xs ${exam.exam_type === 'annual' ? 'bg-red-100 text-red-700' : exam.exam_type === 'half_yearly' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                {examTypes[exam.exam_type] || exam.exam_type}
              </span>
              <span className="text-sm text-gray-500">{exam.start_date}</span>
            </div>
            <h3 className="font-semibold text-lg">{exam.name}</h3>
            <p className="text-gray-500 text-sm mt-1">Max: {exam.max_marks} | Pass: {exam.passing_marks}</p>
            <button onClick={() => setShowMarks(exam)} className="mt-3 text-orange-600 hover:text-orange-700 text-sm">Enter Marks →</button>
          </div>
        ))}
        {exams.length === 0 && <div className="col-span-full text-center py-12 text-gray-500">No exams created yet</div>}
      </div>

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Create Exam">
        <div className="space-y-4">
          <input placeholder="Exam Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          <select value={formData.exam_type} onChange={(e) => setFormData({...formData, exam_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
            {Object.entries(examTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={formData.class_id} onChange={(e) => setFormData({...formData, class_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
            <option value="">Select Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-4">
            <input type="date" placeholder="Start Date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="px-4 py-2 border rounded-lg" />
            <input type="date" placeholder="End Date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="px-4 py-2 border rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input type="number" placeholder="Max Marks" value={formData.max_marks} onChange={(e) => setFormData({...formData, max_marks: parseInt(e.target.value)})} className="px-4 py-2 border rounded-lg" />
            <input type="number" placeholder="Passing Marks" value={formData.passing_marks} onChange={(e) => setFormData({...formData, passing_marks: parseInt(e.target.value)})} className="px-4 py-2 border rounded-lg" />
          </div>
          <button onClick={handleSubmit} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Create Exam</button>
        </div>
      </Modal>

      {showMarks && <MarksEntryModal exam={showMarks} token={token} subjects={subjects} onClose={() => { setShowMarks(null); loadData(); }} />}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Marks Entry Modal
function MarksEntryModal({ exam, token, subjects, onClose }) {
  const [students, setStudents] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadStudents(); }, []);

  const loadStudents = async () => {
    const res = await api.get(`/api/students/?class_id=${exam.class_id}`, token);
    setStudents(res.students || []);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!selectedSubject) return;
    setSaving(true);
    const entries = Object.entries(marks).map(([student_id, marks_obtained]) => ({ student_id, marks_obtained: parseFloat(marks_obtained) || 0 }));
    await api.post('/api/exams/marks/bulk', { exam_id: exam.id, subject_id: selectedSubject, entries }, token);
    setSaving(false);
    onClose();
  };

  if (loading) return <Modal isOpen={true} onClose={onClose} title="Loading..."><div className="p-4 text-center">Loading students...</div></Modal>;

  return (
    <Modal isOpen={true} onClose={onClose} title={`Enter Marks - ${exam.name}`}>
      <div className="space-y-4">
        <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="w-full px-4 py-2 border rounded-lg">
          <option value="">Select Subject</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        
        {selectedSubject && (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Student</th>
                  <th className="px-3 py-2 text-left">Marks (/{exam.max_marks})</th>
                </tr>
              </thead>
              <tbody>
                {students.map(s => (
                  <tr key={s.id} className="border-t">
                    <td className="px-3 py-2">{s.name}</td>
                    <td className="px-3 py-2">
                      <input type="number" min="0" max={exam.max_marks} value={marks[s.id] || ''} onChange={(e) => setMarks({...marks, [s.id]: e.target.value})} className="w-20 px-2 py-1 border rounded" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <button onClick={handleSave} disabled={!selectedSubject || saving} className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Marks'}
        </button>
      </div>
    </Modal>
  );
}

// Timetable Page
function TimetablePage({ token }) {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [toast, setToast] = useState(null);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const periods = [1, 2, 3, 4, 5, 6, 7, 8];

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { if (selectedClass && selectedSection) loadTimetable(); }, [selectedClass, selectedSection]);

  const loadInitialData = async () => {
    try {
      const [classesRes, subjectsRes, teachersRes] = await Promise.all([
        api.get('/api/classes/', token),
        api.get('/api/exams/subjects', token),
        api.get('/api/teachers/', token)
      ]);
      setClasses(classesRes.classes || []);
      setSubjects(subjectsRes.subjects || []);
      setTeachers(teachersRes.teachers || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadTimetable = async () => {
    const res = await api.get(`/api/timetable/class/${selectedClass}/${selectedSection}`, token);
    setTimetable(res.by_day || null);
  };

  const getEntry = (day, period) => {
    if (!timetable || !timetable[day]) return null;
    return timetable[day].find(e => e.period === period);
  };

  const handleAddEntry = async (day, period) => {
    const subject = subjects[0];
    if (!subject) {
      setToast({ message: 'Please add subjects first', type: 'error' });
      return;
    }
    await api.post(`/api/timetable/entry?class_id=${selectedClass}&section_id=${selectedSection}&day=${day}&period=${period}&subject_id=${subject.id}&subject_name=${encodeURIComponent(subject.name)}`, {}, token);
    loadTimetable();
  };

  const sections = classes.find(c => c.id === selectedClass)?.sections || [];

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Timetable</h2>
        <button onClick={() => setEditMode(!editMode)} className={`px-4 py-2 rounded-lg ${editMode ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
          {editMode ? 'Done Editing' : 'Edit Mode'}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        <div className="grid grid-cols-2 gap-4">
          <select value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); setSelectedSection(''); }} className="px-4 py-2 border rounded-lg">
            <option value="">Select Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} className="px-4 py-2 border rounded-lg" disabled={!selectedClass}>
            <option value="">Select Section</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.name}</option>)}
          </select>
        </div>
      </div>

      {selectedClass && selectedSection && (
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left sticky left-0 bg-gray-50">Day</th>
                {periods.map(p => <th key={p} className="px-3 py-2 text-center min-w-24">Period {p}</th>)}
              </tr>
            </thead>
            <tbody>
              {days.map(day => (
                <tr key={day} className="border-t">
                  <td className="px-3 py-2 font-medium capitalize sticky left-0 bg-white">{day}</td>
                  {periods.map(p => {
                    const entry = getEntry(day, p);
                    return (
                      <td key={p} className="px-2 py-2 text-center">
                        {entry ? (
                          <div className="bg-orange-50 rounded p-1">
                            <div className="font-medium text-xs">{entry.subject_name}</div>
                            {entry.teacher_name && <div className="text-xs text-gray-500">{entry.teacher_name}</div>}
                          </div>
                        ) : editMode ? (
                          <button onClick={() => handleAddEntry(day, p)} className="w-full h-8 border-2 border-dashed border-gray-300 rounded text-gray-400 hover:border-orange-400 hover:text-orange-400">+</button>
                        ) : <span className="text-gray-300">-</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!selectedClass && <div className="text-center py-12 text-gray-500">Select a class to view timetable</div>}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Calendar Page
function CalendarPage({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    title: '', title_hindi: '', description: '', event_type: 'event',
    start_date: '', end_date: '', is_holiday: false
  });

  useEffect(() => { loadEvents(); }, [currentMonth]);

  const loadEvents = async () => {
    try {
      const [year, month] = currentMonth.split('-');
      const res = await api.get(`/api/calendar/?year=${year}&month=${month}`, token);
      setEvents(res.events || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.start_date) {
      setToast({ message: 'Title and Date required', type: 'error' });
      return;
    }
    const res = await api.post('/api/calendar/', formData, token);
    if (res.success) {
      setToast({ message: 'Event created!', type: 'success' });
      setShowAdd(false);
      setFormData({ title: '', title_hindi: '', description: '', event_type: 'event', start_date: '', end_date: '', is_holiday: false });
      loadEvents();
    }
  };

  const eventColors = {
    holiday: 'bg-red-100 text-red-700 border-red-200',
    exam: 'bg-amber-100 text-amber-700 border-amber-200',
    event: 'bg-blue-100 text-blue-700 border-blue-200',
    ptm: 'bg-purple-100 text-purple-700 border-purple-200',
    vacation: 'bg-pink-100 text-pink-700 border-pink-200',
    sports: 'bg-teal-100 text-teal-700 border-teal-200',
    cultural: 'bg-orange-100 text-orange-700 border-orange-200'
  };

  const eventTypes = ['holiday', 'event', 'exam', 'ptm', 'vacation', 'sports', 'cultural', 'meeting'];

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Academic Calendar</h2>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">+ Add Event</button>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between">
          <button onClick={() => { const d = new Date(currentMonth + '-01'); d.setMonth(d.getMonth() - 1); setCurrentMonth(d.toISOString().slice(0, 7)); }} className="px-3 py-1 rounded hover:bg-gray-100">← Prev</button>
          <input type="month" value={currentMonth} onChange={(e) => setCurrentMonth(e.target.value)} className="px-4 py-2 border rounded-lg" />
          <button onClick={() => { const d = new Date(currentMonth + '-01'); d.setMonth(d.getMonth() + 1); setCurrentMonth(d.toISOString().slice(0, 7)); }} className="px-3 py-1 rounded hover:bg-gray-100">Next →</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map(event => (
          <div key={event.id} className={`rounded-xl p-4 border ${eventColors[event.event_type] || 'bg-gray-100'}`}>
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs uppercase font-medium">{event.event_type}</span>
              {event.is_holiday && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">Holiday</span>}
            </div>
            <h3 className="font-semibold">{event.title}</h3>
            {event.title_hindi && <p className="text-sm opacity-75">{event.title_hindi}</p>}
            <p className="text-sm mt-2">{new Date(event.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            {event.description && <p className="text-sm mt-2 opacity-75">{event.description}</p>}
          </div>
        ))}
        {events.length === 0 && <div className="col-span-full text-center py-12 text-gray-500">No events this month</div>}
      </div>

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add Calendar Event">
        <div className="space-y-4">
          <input placeholder="Title (English)" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          <input placeholder="Title (Hindi)" value={formData.title_hindi} onChange={(e) => setFormData({...formData, title_hindi: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          <select value={formData.event_type} onChange={(e) => setFormData({...formData, event_type: e.target.value, is_holiday: e.target.value === 'holiday'})} className="w-full px-4 py-2 border rounded-lg">
            {eventTypes.map(t => <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-4">
            <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="px-4 py-2 border rounded-lg" />
            <input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="px-4 py-2 border rounded-lg" placeholder="End Date (optional)" />
          </div>
          <textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-20" />
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={formData.is_holiday} onChange={(e) => setFormData({...formData, is_holiday: e.target.checked})} className="w-4 h-4" />
            <span>Mark as Holiday</span>
          </label>
          <button onClick={handleSubmit} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Add Event</button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Wallet Page - Complete Wallet Management

export default BulkImportPage;
